<template>
  <v-card>
    <v-card-title :class="contractTypeColor">
      <v-row justify="space-between" align="center">
        <v-col cols="auto">
          <span class="headline">{{ title }} </span>
        </v-col>

        <v-col>
          <ActivityFormContractHeader :contract="contract"/>
        </v-col>

        <v-col cols="auto">
          <BaseDialogActions hideRefresh/>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="pt-5">
      <ContractRateChangeActivityTable
      isImmediateRateChange
      :contract="contract"
      @changed="onActivityChanged"
      @effective-date-changed="onEffectiveDateChanged"
      @scheduled-rate-change-deleted="onScheduledChangeDeleted"
      />
    </v-card-text>

    <v-card-actions class="py-4 px-0" v-if="!scheduledRateChangeAlreadyExistsForContract">
      <v-row justify="end">
        <v-col cols="auto">
          <v-btn :class="contractTypeColor" @click="saveClicked">
            {{ $t('save') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>

    <Dialog :stateId="uncoveredCombinationsDialogId" @close="uncoveredCombinations = null">
      <UncoveredLogCombinationsDialog
      v-if="uncoveredCombinations"
      :gap="uncoveredCombinations"/>
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { companyOffsetToLocalDate } from '@/utils/DateFormatter.js'
import moment from 'moment'
import {
  ActivitiesUpdateRequest,
  ActivityCreateRequest, ActivityUpdateRequest,
  ActivityDetailCreateRequest, ActivityDetailUpdateRequest,
  CostCalculationCreateRequest, CostCalculationUpdateRequest
} from '@/components/activity/Activity'
import { TemplateSpecialization, AccountingCategory, ContractType } from '@/utils/Enumerations.js'
import { colorClassForContractType, uniqueDialogId } from '@/utils/componentHelpers.js'
import { expansionCoverageGapFromError } from '@/utils/ExpansionCoverage.js'

export default {
  name: 'ContractRateChangeForm',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ActivityFormContractHeader: () => import('@/components/activity/ActivityFormContractHeader.vue'),
    ContractRateChangeActivityTable: () => import('@/components/contract/scheduled-rate-change/ContractRateChangeActivityTable.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    UncoveredLogCombinationsDialog: () => import('@/components/activity/rate-sheet/UncoveredLogCombinationsDialog.vue')
  },

  props: {
    contract: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    effectiveDate: null,
    effectiveDateDefaultStart: moment().startOf('day').add(7, 'days'),
    activities: [],
    uncoveredCombinations: null,
    uncoveredCombinationsDialogId: uniqueDialogId('rate-change-uncovered-log-combinations')
  }),

  computed: {
    ...mapGetters('activity', ['allActivities', 'activitiesByEntity']),
    ...mapGetters('scheduled-rate-change', ['contractScheduledRateChanges']),

    scheduledRateChangeAlreadyExistsForContract () {
      return this.allActivities.some(a => {
        const hasChange = a.baseCost?.scheduledRateChangeId > 0
        const detailHasChange = a.activityDetails?.some(d => {
          return d.cost?.scheduledRateChangeId > 0
        })
        return hasChange || detailHasChange
      })
    },

    contractTypeColor () {
      return colorClassForContractType(this.contract.type)
    },

    title () {
      switch (this.contract.type) {
        case ContractType.Production.value:
        case ContractType.WoodsSale.value:
          return this.$t('editingLogsContract')
        case ContractType.ByproductSale.value:
        case ContractType.ByproductPurchase.value:
        case ContractType.ByproductHauling.value:
          return this.$t('editingByproductContract')
        case ContractType.Transfer.value:
          return this.$t('editingTransferContract')
        case ContractType.LogYardSale.value:
          return this.$t('editingLogYardSaleContract')
      }

      return ''
    }
  },

  async mounted () {
    if (this.contractScheduledRateChanges) {
      this.effectiveDate = this.contractScheduledRateChanges.effectiveDate
    }
  },

  methods: {
    companyOffsetToLocalDate,
    ...mapActions('activity', ['updateActivities']),
    ...mapActions('dialog', ['openOrUpdateDialog']),

    async saveClicked () {
      if (!this.validate()) {
        return
      }

      const contractId = this.contract.contractId
      const deletedActivityIds = this.activities.filter(a => a.isRemoved).map(a => a.activityId)
      const updatedActivities = this.getUpdatedActivities()
      const newActivities = this.getNewActivities()

      const requestObj = new ActivitiesUpdateRequest({
        contractId,
        deletedActivityIds,
        updatedActivities,
        newActivities
      })
      try {
        await this.updateActivities(requestObj)
      } catch (error) {
        this.showUncoveredCombinations(error)
        return
      }

      this.$emit('save')
    },

    showUncoveredCombinations (error) {
      const gap = expansionCoverageGapFromError(error)
      if (!gap) throw error

      this.uncoveredCombinations = gap
      this.openOrUpdateDialog({ id: this.uncoveredCombinationsDialogId, width: '900px' })
    },

    validate () {
      if (this.noChangesToSave()) {
        this.setSnackError(this.$t('noChangesToSave'))
        return false
      }

      return true
    },

    noChangesToSave () {
      const atLeastOneChangedActivity = this.activities.some(a => a.hasBeenChanged)
      const atLeastOneRemovedActivity = this.activities.some(a => a.isRemoved)
      const atLeastOneNewActivity = this.activities.some(a => !a.activityId)

      const atLeastOneChangedDetail = this.activities.some(a => a?.activityDetails?.some(d => d.hasBeenChanged))
      const atLeastOneNewDetail = this.activities.some(a => a?.activityDetails?.some(d => !d.activityDetailId))
      const atLeastOneRemovedDetail = this.activities.some(a => a?.activityDetails?.some(d => d.isRemoved))

      return !(atLeastOneChangedActivity || atLeastOneRemovedActivity || atLeastOneNewActivity ||
        atLeastOneChangedDetail || atLeastOneNewDetail || atLeastOneRemovedDetail)
    },

    getUpdatedActivities () {
      const deletedActivityIds = new Set(
        this.activities.filter(a => a.isRemoved && a.activityId).map(a => a.activityId)
      )

      return this.activities
        .filter(a => !a.isRemoved &&
          !!a.activityId &&
          (a.hasBeenChanged ||
            deletedActivityIds.has(a.deductFromActivityId) ||
            a?.activityDetails
              ?.some(d => d.hasBeenChanged ||
                d.isRemoved ||
                !d.activityDetailId)))
        .map(activity => {
          const severDeduction = deletedActivityIds.has(activity.deductFromActivityId)
          return new ActivityUpdateRequest({
            effectiveDate: activity.effectiveDate,
            contractId: this.contract.contractId,
            activityId: activity.activityId,
            templateId: activity.activityTemplate.activityTemplateId,
            contractorId: this.getCleanContractorId(activity),
            runtimeCounterparty: this.getCleanRuntimeCounterparty(activity),
            recoveryMode: this.getCleanRecoveryMode(activity),
            advanceId: activity.advanceId,
            activityDetails: this.getDetailUpdateForActivity(activity),
            baseCost: this.getBaseCostUpdateForActivity(activity),
            deductFromActivityIdentifier: severDeduction ? null : activity.deductFromActivityIdentifier,
            deductFromActivityId: severDeduction ? null : activity.deductFromActivityId,
            namedRateId: activity.namedRateId
          })
        })
    },

    getCleanContractorId (activity) {
      return activity.runtimeCounterparty != null ? undefined : activity.contractor?.accountId
    },

    getCleanRuntimeCounterparty (activity) {
      return activity.runtimeCounterparty != null ? activity.runtimeCounterparty : null
    },

    getCleanRecoveryMode (activity) {
      return typeof (activity.recoveryMode) === 'number'
        ? activity.recoveryMode
        : activity.recoveryMode?.value
    },

    getDetailUpdateForActivity (activity) {
      return activity.activityDetails ? activity.activityDetails.map(d => {
        return d.isRemoved
          ? null
          : new ActivityDetailUpdateRequest({
            activityDetailId: d.activityDetailId,
            productId: d.product.productId,
            rate: d.cost.rate
          })
      }).filter(i => i) : []
    },

    getBaseCostUpdateForActivity (activity) {
      if (activity.baseCost.rateSheet && typeof activity.baseCost.rateSheet === 'string') {
        activity.baseCost.rateSheet = {
          rateSheetLines: [],
          rateSheetId: activity.baseCost.rateSheetId
        }
      }

      return activity.baseCost ? new CostCalculationUpdateRequest({
        rate: activity.baseCost.rate,
        payBy: activity.baseCost.payBy,
        payOn: activity.baseCost.payOn,
        rateSheet: activity.baseCost.rateSheet ? {
          rateSheetDefinitionId: activity.baseCost.rateSheet?.rateSheetDefinitionId,
          rateSheetLines: activity.baseCost.rateSheet?.rateSheetLines ?? [],
          rateSheetId: activity.baseCost.rateSheet?.rateSheetId
        } : null
      }) : null
    },

    getNewActivities () {
      return this.activities
        .filter(a => !a.isRemoved && !a.activityId)
        .map(activity => {
          return new ActivityCreateRequest({
            effectiveDate: activity.effectiveDate,
            contractId: this.contract.contractId,
            templateId: activity.activityTemplate.activityTemplateId,
            contractorId: activity.contractor?.accountId,
            runtimeCounterparty: null, // activity.runtimeCounterparty, - We don't support runtime counterparty for activities not made in bundle ui
            recoveryMode: this.getCleanRecoveryMode(activity),
            advanceId: activity.advanceId,
            activityDetails: this.getDetailRequestForNewActivity(activity),
            baseCost: this.getBaseCostRequestForNewActivity(activity),
            deductFromActivityIdentifier: activity.deductFromActivityIdentifier,
            deductFromActivityId: activity.deductFromActivityId,
            namedRateId: activity.namedRateId
          })
        })
    },

    getDetailRequestForNewActivity (activity) {
      return activity.activityDetails ? activity.activityDetails.map(d => {
        return d.isRemoved
          ? null
          : new ActivityDetailCreateRequest({
            productId: d.product.productId,
            rate: d.cost.rate
          })
      }).filter(i => i) : []
    },

    getBaseCostRequestForNewActivity (activity) {
      if (activity.activityTemplate.specialization === TemplateSpecialization.Transfer.value ||
          (activity.activityTemplate.specialization === TemplateSpecialization.Penalty.value && activity.activityTemplate.category === AccountingCategory.Payable.value)) {
        return null
      }
      return activity.baseCost ? new CostCalculationCreateRequest({
        rate: activity.baseCost.rate,
        payBy: activity.baseCost.payBy,
        payOn: activity.baseCost.payOn,
        rateSheet: activity.baseCost.rateSheet ? {
          rateSheetDefinitionId: activity.baseCost.rateSheet.rateSheetDefinitionId,
          effectiveDate: activity.effectiveDate,
          rateSheetLines: activity.baseCost.rateSheet.rateSheetLines
        } : null
      }) : null
    },

    onActivityChanged (updatedActivities) {
      this.activities = updatedActivities
    },

    onEffectiveDateChanged (newDate) {
      this.effectiveDate = newDate
    },

    onScheduledChangeDeleted () {
      this.$emit('scheduled-rate-change-deleted')
    }
  }
}

</script>
